/* eslint-disable implicit-arrow-linebreak */
import 'video.js/dist/video-js.css';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { render } from 'react-dom';
import { VIDEO_QUALITIES } from 'src/helpers/constants';
import { getVideoSetting, setVideoSetting } from 'src/helpers/videoSetting';
import appActions from 'src/redux/app/actions';
import { RootState } from 'src/redux/reducer';
import { ReadyState } from 'src/types/videojs';
import videojs, { VideoJsPlayer } from 'video.js';

import InteractiveContent from '../interactiveContent';

import CustomMenu from './customMenu';
import CustomProgressControlComponent from './customProgressControl';
import InteractiveButton from './interactiveButton';
import QualityControlComponent from './qualityControl';
import VideoJsStyle from './style';
import VideoMarker from './videoMarker';
import { CustomVolumePanel } from './volumePanel';

import Watermark from '@components/organisms/learningPage/watermark';
import { IInteractiveContent, IInteractiveContentSubmissions, ISubtitle, VideoSetting } from '@interfaces/learning';
import InteractiveContentActions from '@redux/interactiveContent/actions';

const VideoJs = forwardRef<any, any>((props, ref) => {
  const {
    videoUrl,
    subtitles = [],
    textTrackLabel,
    thumbUrl,
    seekTime,
    playbackRate,
    isFluid,
    onPlaying,
    onPause,
    onEnded,
    onLoadedSrc,
    onChangePlaybackRate,
    onChangeVolume,
    onChangeTextTrackLabel,
    onUpdateUserActivity,
    onMouseDown,
    onMouseUp,
    stateCurriculum,
    videoQualities,
    onNextChapter,
    onPreviousChapter,
    onCloseInteractiveModal,
    onPlayerDispose,
    isPreview,
    interactiveContents = [],
    interactiveContentSubmissions = [],
    onTimeUpdate,
    watermarkInfo,
    isShowNextChapterButton,
    isShowPreviousChapterButton,
    volume,
    quality,
    isMuted,
    className = '',
  } = props;

  const { set_is_dialog_open } = appActions;
  const dispatch = useDispatch();
  const { interactiveContentSubmission } = useSelector((state: RootState) => state.InteractContent);

  const videoContainerRef = useRef<HTMLDivElement>();
  const videoRef = useRef<any>();
  const setupPlaybackRateTimeout = useRef<any>();

  const isEndedRef = useRef<boolean>(false);
  const displayedInteractionsRef = useRef<(IInteractiveContent | IInteractiveContentSubmissions)[]>([]);

  const [isLoadedFirstFrame, setIsLoadedFirstFrame] = useState(false);
  const [isPlayed, setIsPlayed] = useState(false);
  const [isOpenInteraction, setIsOpenInteraction] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentInteraction, setCurrentInteraction] = useState<IInteractiveContent | null>(null);
  const [availableInteraction, setAvailableInteraction] = useState<IInteractiveContent | null>(null);
  const [isDisplayCustomMenu, setIsDisplayCustomMenu] = useState<boolean>(false);

  const SKIP_SEC = 15;

  const createControlChild = () => {
    const controlPlayer: Record<string, any> = {
      playToggle: {},
      customVolumePanel: {
        inline: false,
      },
      currentTimeDisplay: {},
      customProgressControl: {
        keepTooltipsInside: true,
      },
      durationDisplay: {},
      qualityControl: undefined,
      playbackRateMenuButton: {},
      captionsButton: {},
      fullscreenToggle: {},
    };

    if (videoQualities) {
      controlPlayer.qualityControl = {
        qualities: videoQualities,
      };
    }

    return controlPlayer;
  };

  const handleSkipTime = (value: number) => {
    const player = videojs(videoRef?.current);
    const currentTime = player.currentTime();
    const maxDuration = player.duration();
    let updateDuration = currentTime + value;

    if (updateDuration >= maxDuration) {
      updateDuration = maxDuration - 2;
    } else if (updateDuration < 0) {
      updateDuration = 0;
    }

    player.currentTime(updateDuration);
  };

  const addEventListenerTextTrack = (tracks: TextTrackList) => {
    const trackSize = tracks.length;
    let isEnabled = false;
    let currentLabelTextTrack = textTrackLabel;

    for (let i = 0; i < trackSize; i++) {
      const track = tracks[i];

      if (track.kind === 'captions' && track.mode === 'showing') {
        currentLabelTextTrack = track.label;
        isEnabled = true;
      }
    }

    updateVideoSetting({ textTrackLabel: isEnabled ? currentLabelTextTrack : null });
  };

  const clearRemoteTextTracks = (player: VideoJsPlayer, textTracks: TextTrackList) => {
    for (let i = 0; i < textTracks.length; i++) {
      const track = textTracks[i];
      // Check if the track kind is 'captions' or 'subtitles'
      if (track.kind === 'captions' || track.kind === 'subtitles') {
        // Clear all cues in the text track
        player.removeRemoteTextTrack((track as unknown) as HTMLTrackElement);
      }
    }
  };

  const setupRemoteTextTracks = () => {
    const player = videojs(videoRef?.current);
    player.ready(() => {
      const textTracks = player.textTracks();
      let currentLabelTextTrack = textTrackLabel;

      clearRemoteTextTracks(player, textTracks);

      textTracks.addEventListener('change', () => addEventListenerTextTrack(textTracks));

      const haveSubtitle = subtitles.some((subtitle: ISubtitle) => currentLabelTextTrack === subtitle.label);

      if (!haveSubtitle && currentLabelTextTrack) {
        currentLabelTextTrack = subtitles[0]?.label || null;
      }

      subtitles.forEach((subtitle: ISubtitle) => {
        const isSubtitleShowing = currentLabelTextTrack === subtitle.label;
        player.addRemoteTextTrack(
          {
            kind: 'captions',
            label: subtitle.label,
            src: subtitle.src,
            mode: isSubtitleShowing ? 'showing' : 'disabled',
          },
          true,
        );
      });

      updateVideoSetting({ textTrackLabel: currentLabelTextTrack });
    });
  };

  const openInteractionContent = (interaction: IInteractiveContent) => {
    const isDisplayed = displayedInteractionsRef.current.some((item) => {
      const isArticleDisplayed = !!item.article_id && item.article_id === interaction.article_id;
      const isQuizDisplayed = !!item.quiz_id && item.quiz_id === interaction.quiz_id;

      return isArticleDisplayed || isQuizDisplayed;
    });

    if (!isDisplayed) {
      displayedInteractionsRef.current = [...displayedInteractionsRef.current, interaction];
    }

    setCurrentInteraction(interaction);
    setIsOpenInteraction(true);
  };

  const handleSetInteraction = useCallback(
    (interaction: IInteractiveContent) => {
      const player = videojs(videoRef?.current);

      player.currentTime(interaction.display_point);
      player.one('seeked', () => openInteractionContent(interaction));
    },
    [displayedInteractionsRef.current],
  );

  const handleClickPlay = () => {
    const player = videojs(videoRef.current);
    const isPaused = player.paused();

    if (isPaused && !currentInteraction) {
      player.play();
    } else {
      player.pause();
    }
  };

  const updateVideoSetting = (params?: VideoSetting) => {
    const player = videojs(videoRef.current);
    const videoSetting: VideoSetting = getVideoSetting();

    setVideoSetting({
      textTrackLabel,
      volume: player.volume(),
      playbackRate: player.playbackRate(),
      isMuted: player.muted(),
      quality: videoSetting ? videoSetting.quality : VIDEO_QUALITIES.AUTO,
      ...(params || {}),
    });
  };

  const handleVisibleCustomMenuOnPaused = (playerDOM: any) => {
    const isPaused = playerDOM.paused();

    if (isPaused) {
      setIsDisplayCustomMenu(true);
    }
  };

  const checkAvailableInteractions = async (currentTime: number): Promise<boolean> => {
    const availableInteraction = interactiveContents.find(
      (item: IInteractiveContent, index: number, arr: IInteractiveContent[]) => {
        const nextItem = arr[index + 1];
        if (nextItem?.display_point <= currentTime) {
          return false;
        }
        const elapsedTime = currentTime - item.display_point;
        return elapsedTime >= 0 && elapsedTime <= 10;
      },
    );

    const isSameTime = Number(currentTime.toFixed()) === availableInteraction?.display_point;

    const isDisplayed = displayedInteractionsRef.current.some((item) => {
      const isArticleDisplayed =
        !!availableInteraction?.article_id && availableInteraction.article_id === item.article_id;
      const isQuizDisplayed = !!availableInteraction?.quiz_id && availableInteraction.quiz_id === item.quiz_id;
      return isArticleDisplayed || isQuizDisplayed;
    });

    const hasAvailableInteractiveContent = Boolean(isSameTime && availableInteraction && !isDisplayed);

    if (hasAvailableInteractiveContent) {
      handleSetInteraction(availableInteraction);
    }

    setAvailableInteraction(availableInteraction || null);

    return hasAvailableInteractiveContent;
  };

  const handleInteractiveContentLastSecond = async () => {
    const player = videojs(videoRef?.current);
    onUpdateUserActivity?.(true);

    const hasAvailableInteraction = await checkAvailableInteractions(player.duration());

    if (!hasAvailableInteraction) {
      onEnded?.();
    }
  };

  const setupPlaybackRate = (rate: any) => {
    setupPlaybackRateTimeout.current = setTimeout(() => {
      const player = videojs(videoRef.current);

      if (player.readyState() === ReadyState.HaveEnoughData) {
        player.playbackRate(rate);
      } else {
        setupPlaybackRate(rate);
      }
    }, 500);
  };

  const onClose = () => {
    if (currentInteraction && interactiveContentSubmission?.id) {
      const finishedAt = new Date().toISOString();
      const payload = {
        finished_at: finishedAt,
        type: currentInteraction.interaction_type,
      };

      dispatch(
        InteractiveContentActions.close_interactive_content(
          currentInteraction.id,
          interactiveContentSubmission.id,
          payload,
        ),
      );
    } else {
      dispatch(InteractiveContentActions.clean_interactive_content_submission());
    }

    dispatch(set_is_dialog_open(false));

    setCurrentInteraction(null);
    onCloseInteractiveModal?.();
  };

  const setupVideoJs: any = async (playerDOM: any) => {
    playerDOM.on(['click', 'touchstart'], () => {
      document.querySelector('.vjs-volume-panel')?.classList.remove('vjs-volume-panel-active', 'vjs-hover');
    });

    playerDOM.one('play', () => {
      setIsPlayed(true);
    });

    playerDOM.on('loadedmetadata', () => {
      playbackRate && playerDOM.playbackRate(playbackRate);
      onLoadedSrc?.();
    });

    playerDOM.on('loadeddata', () => {
      setIsLoadedFirstFrame(true);
    });

    playerDOM.on('playing', () => {
      onPlaying?.();
      setIsLoadedFirstFrame(false);
    });

    playerDOM.on(['waiting', 'pause'], (event: any) => {
      onPause?.();
    });

    playerDOM.on('waiting', (event: any) => {
      setIsDisplayCustomMenu(false);
    });

    playerDOM.on('timeupdate', (_event: any) => {
      const readyState = playerDOM.readyState();

      if (readyState !== ReadyState.HaveEnoughData) return;
      setCurrentTime(playerDOM.currentTime());

      onUpdateUserActivity?.(playerDOM.userActive_);
      onTimeUpdate?.(playerDOM.currentTime(), playerDOM.duration());
    });

    playerDOM.on('ratechange', () => {
      onChangePlaybackRate?.(videoRef.current.playbackRate);
      updateVideoSetting();
    });

    playerDOM.on('ended', () => {
      isEndedRef.current = true;
      handleInteractiveContentLastSecond();
    });

    playerDOM.on('canplay', () => {
      setIsDisplayCustomMenu(true);
    });

    playerDOM.on('volumechange', () => {
      onChangeVolume?.(videoRef.current.volume);
      updateVideoSetting();
    });

    playerDOM.on('mousedown', () => {
      onMouseDown?.();
    });

    playerDOM.on('mouseup', () => {
      onMouseUp?.();
    });

    playerDOM.on('mousemove', () => {
      handleVisibleCustomMenuOnPaused(playerDOM);
    });

    playerDOM.on('touchstart', () => {
      handleVisibleCustomMenuOnPaused(playerDOM);
    });

    playerDOM.on('userinactive', () => {
      const isPaused = playerDOM.paused();

      if (isPaused) {
        setIsDisplayCustomMenu(false);
      }
    });
  };

  const registerVideoJs = (): VideoJsPlayer => {
    videojs.registerComponent('QualityControl', QualityControlComponent);
    videojs.registerComponent('CustomProgressControl', CustomProgressControlComponent);
    videojs.registerComponent('CustomVolumePanel', CustomVolumePanel);

    const children = createControlChild();

    const videoJsOptions: any = {
      playsinline: true,
      fluid: isFluid,
      autoplay: true,
      muted: false,
      controls: true,
      techOrder: ['html5', 'flash'],
      flash: {
        swf: 'https://s3-ap-southeast-1.amazonaws.com/skilllane/videojs/video-js.swf',
      },
      poster: thumbUrl,
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
      textTrackSettings: false,
      controlBar: { children },
      resizeManager: false, // Remove resizeManager(iFrame) has overlap custom menu.
      html5: {
        hls: {
          overrideNative: !videojs.browser.IS_SAFARI, // For Android Chrome native is not supported playback rate.
        },
      },
      inactivityTimeout: 3000,
    };

    videojs.hook('setup', setupVideoJs);
    const tempPlayer = videojs(videoRef.current, videoJsOptions);
    return tempPlayer;
  };

  useEffect(() => {
    const player = registerVideoJs();
    const otherPlayers = videojs.getAllPlayers();
    for (const otherPlayer of otherPlayers) {
      if (player !== otherPlayer) {
        otherPlayer.pause();
      }
    }

    return () => {
      videojs.removeHook('setup', setupVideoJs);
      player.dispose();
      onPlayerDispose?.();
    };
  }, []);

  useEffect(() => {
    if (interactiveContentSubmissions.length && !isPreview) {
      displayedInteractionsRef.current = [...interactiveContentSubmissions];
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentElement = document.activeElement;
      if (currentElement?.tagName !== 'VIDEO') return;

      const player = videojs(videoRef.current);
      switch (event.key) {
        case ' ': // Spacebar toggle play/pause
          player.paused() ? player.play() : player.pause();
          break;
        case 'ArrowLeft':
          player.currentTime(player.currentTime() - SKIP_SEC); // Backward
          break;
        case 'ArrowRight':
          player.currentTime(player.currentTime() + SKIP_SEC); // Forward
          break;
        case 'ArrowUp':
          player.volume(player.volume() + 0.1); // Increase volume
          break;
        case 'ArrowDown':
          player.volume(player.volume() - 0.1); // Decrease volume
          break;
        case 'f':
        case 'F':
          if (document.fullscreenElement) {
            document.exitFullscreen(); // Exit fullscreen
          } else {
            player.requestFullscreen(); // Enter fullscreen
          }
          break;
        case 'm':
        case 'M':
          player.muted(!player.muted()); // Toggle mute
          break;
        default:
          if (/^[0-9]$/.test(event.key)) {
            const seekPercentage = parseInt(event.key) * 0.1; // Seek to a percentage (0-9)
            const duration = player.duration();
            if (!isNaN(seekPercentage) && duration) {
              player.currentTime(duration * seekPercentage);
            }
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const player = videojs(videoRef.current);
    checkAvailableInteractions(currentTime);

    const dialogElement = document.getElementsByClassName('MuiModal-root')[0];
    const isDialogOpen = dialogElement && dialogElement.className.startsWith('MuiModal-root MuiDialog-root');

    if (currentInteraction || isDialogOpen) {
      player.pause();
    }
  }, [currentTime, displayedInteractionsRef.current, interactiveContents]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    const player = videojs(videoRef.current);
    if (videoUrl) {
      player.src({
        src: videoUrl,
        type: 'application/x-mpegURL',
      });

      setupPlaybackRate(playbackRate);
      player.volume(volume);
      player.muted(isMuted);
      setupRemoteTextTracks();
    }
    setCurrentInteraction(null);
    setAvailableInteraction(null);

    return () => {
      setupPlaybackRateTimeout.current && clearTimeout(setupPlaybackRateTimeout.current);
    };
  }, [videoUrl]);

  useEffect(() => {
    if (!videoRef.current) return;
    const player = videojs(videoRef.current);

    const getVideoMarkerContainer = () => {
      let markerContainerEl = document.querySelector('.vjs-marker-container');

      if (!markerContainerEl) {
        markerContainerEl = document.createElement('div');
        markerContainerEl.className = 'vjs-marker-container';
        const progressControlEl = player.controlBar.getChild('CustomProgressControl')?.el();
        progressControlEl?.appendChild(markerContainerEl);
      }

      return markerContainerEl;
    };

    const renderVideoMarker = () => {
      const container = getVideoMarkerContainer();
      if (!container) return;

      render(
        <VideoMarker
          interactiveContents={interactiveContents}
          displayedInteractions={displayedInteractionsRef.current}
          duration={player.duration()}
          onMarkerClick={handleSetInteraction}
        />,
        container,
      );
    };

    if (player.readyState() >= 1) {
      renderVideoMarker();
    } else {
      player.one('loadedmetadata', renderVideoMarker);
    }
    player.on('durationchange', renderVideoMarker);

    return () => {
      player.off(['loadedmetadata', 'durationchange'], renderVideoMarker);
    };
  }, [displayedInteractionsRef.current, handleSetInteraction, interactiveContents]);

  useEffect(() => {
    const player = videojs(videoRef.current);
    if (isEndedRef.current && !currentInteraction) return onEnded?.();
    if (currentInteraction) {
      player.pause();
    } else {
      player.play();
    }
  }, [currentInteraction]);

  useEffect(() => {
    const player = videojs(videoRef?.current);
    const control = player.controlBar.getChild('QualityControl') as QualityControlComponent | undefined;
    control?.setQualities(videoQualities);
    if (quality) {
      control?.onSetQuility(quality);
    }
    control?.update();
  }, [videoQualities]);

  useEffect(() => {
    if (!isLoadedFirstFrame) return;
    const player = videojs(videoRef.current);
    player.currentTime(seekTime);
  }, [isLoadedFirstFrame]);

  return (
    <VideoJsStyle className={className}>
      <Watermark watermarkInfo={watermarkInfo} />
      <div data-vjs-player>
        <video ref={videoRef} className="video-js vjs-big-play-centered" />

        {isPlayed && (
          <>
            {isDisplayCustomMenu && (
              <CustomMenu
                onSkipTimeClick={handleSkipTime}
                stateCurriculum={stateCurriculum}
                onNextChapterClick={onNextChapter}
                onPrevChapterClick={onPreviousChapter}
                onPlayPauseButtonClick={handleClickPlay}
                isPaused={videojs(videoRef.current).paused()}
                isShowNextChapterButton={isShowNextChapterButton}
                isShowPreviousChapterButton={isShowPreviousChapterButton}
              />
            )}

            {currentInteraction && (
              <InteractiveContent
                isPreview={isPreview}
                isOpen={isOpenInteraction}
                interactiveContent={currentInteraction}
                containerRef={videoContainerRef}
                onClose={onClose}
              />
            )}

            {!isOpenInteraction && availableInteraction && (
              <InteractiveButton
                title={availableInteraction?.name}
                interactionType={availableInteraction?.interaction_type}
                onClick={() => openInteractionContent(availableInteraction)}
              />
            )}
          </>
        )}
      </div>
    </VideoJsStyle>
  );
});

VideoJs.propTypes = {
  isFluid: PropTypes.bool,
  seekTime: PropTypes.number,
  playbackRate: PropTypes.number,
  stateCurriculum: PropTypes.string,
  isShowCustomMenu: PropTypes.bool,
  onPlaying: PropTypes.func,
  onPause: PropTypes.func,
  onEnded: PropTypes.func,
  onLoadedSrc: PropTypes.func,
  onChangePlaybackRate: PropTypes.func,
  onUpdateUserActivity: PropTypes.func,
  videoQualities: PropTypes.array,
  onNextChapter: PropTypes.func,
  onPreviousChapter: PropTypes.func,
  isCustomHideNextButton: PropTypes.bool,
  isPreview: PropTypes.bool,
  interactiveContents: PropTypes.array,
  subtitles: PropTypes.array,
  volume: PropTypes.number,
  quality: PropTypes.string,
  isMuted: PropTypes.bool,
  className: PropTypes.string,
};

VideoJs.defaultProps = {
  quality: VIDEO_QUALITIES.AUTO,
  isMuted: false,
  volume: 1,
  playbackRate: 1.0,
};

export default VideoJs;
